import {space} from "../../../../provider_api";
import React from "react";
import {Editable, EditableProps} from "../../../../components/Editable/Editable";
import {useEditable} from "../../../../components/Editable/EditableContext";
import {dayOfWeekNames, dayOfWeekValues} from "../../../../util/DateTimeUtils";
import Select from "react-select";
import RepeatStrategyProto = space.kenko.proto.RepeatStrategyProto;
import DayOfWeekProto = space.kenko.proto.DayOfWeekProto;
import IWeeklyRepeatSchedule = space.kenko.proto.IWeeklyRepeatSchedule;
import IRepeatStrategyProto = space.kenko.proto.IRepeatStrategyProto;
import {findDefaultValue} from "../../../../util/ProviderUtil";

type Props<EnclosingType extends object> = EditableProps<EnclosingType, IRepeatStrategyProto> & {
  // This is the original time/date of the event being created, which is a hint
  // to populate the weekly repeat schedule with this day.
  startTimestamp: number
}

export const RepeatStrategyEditor = <EnclosingType extends object>(props: Props<EnclosingType>) => {

  const {editing, mode, value, setValue} = useEditable(props);

  // Uh. First day of the event instance? 
  const firstDay = new Date(props.startTimestamp as number);

  const options = [{
    value: 'none',
    label: 'Does not repeat'
  }, {
    value: 'weekly',
    label: 'Weekly'
  }]

  return <Editable {...props}>

    {editing && !props.immutable && <>

      <Select
          defaultValue={findDefaultValue(options, value ? (value as RepeatStrategyProto).RepeatStrategy : undefined)}
          options={options}
          className="EditableMultiSelect"
          classNamePrefix={'Editable'}
          onChange={value => {
            if (value?.value == 'weekly') {
              setValue(new RepeatStrategyProto({
                weekly: {
                  repeatPeriodWeeks: 1,
                  daysOfWeekBitmask: toBitMask(toDayOfWeek(firstDay))
                },
                none: undefined,
              }));
            } else if (value?.value == 'none') {
              setValue(new RepeatStrategyProto({
                none: {},
              }));
            }
          }}/>

      {value && value.weekly &&
          <WeeklyEditor proto={value.weekly}
                        onProtoChange={newProto => setValue(new RepeatStrategyProto({
                          weekly: newProto
                        }))}
                        initialDayOfWeek={DayOfWeekProto.MONDAY}/>}

    </>}

    {(!editing || props.immutable) &&
        <div className={'EditableNotEditing'}>{getLabelForRepeatStrategy(value)}</div>
    }

  </Editable>
}

const getLabelForRepeatStrategy = (repeatStrategy: IRepeatStrategyProto | null | undefined): string => {

  if (repeatStrategy?.weekly) {
    const repeatPeriodWeeks = repeatStrategy.weekly.repeatPeriodWeeks;
    const daysOfWeekBitmask = repeatStrategy.weekly.daysOfWeekBitmask ?? 0;

    const dayStrings: string[] = []

    dayOfWeekValues.map(value => {
      if ((daysOfWeekBitmask & toBitMask(value)) > 0) {
        dayStrings.push(dayOfWeekNames[value] as string);
      }
    });

    const repeatPeriodString = repeatPeriodWeeks == 1 ? 'weekly' : `every ${repeatPeriodWeeks} weeks`;

    return `Repeats ${repeatPeriodString} on ${dayStrings.join(', ')}`;
    
  } else if (repeatStrategy?.none) {
    return 'Does not repeat';
  }
  
  return 'N/A';

}

// Returns the value in the week bitmask of the given day
const toBitMask = (day: DayOfWeekProto): number => {
  if (day == 0) {
    throw new Error("why is day 0");
  }
  return (1 << (day - 1));
}

// javascript and ISO are different/
// weirdly 1-6 are the same (MONDAY - SUNDAY) but in javascript's
// date 0 is sunday, vs ISO where 7 is sunday.
const toDayOfWeek = (date: Date): DayOfWeekProto => {
  if (date.getDay() == 0) {
    return DayOfWeekProto.SUNDAY;
  }

  return date.getDay();
}


type WeeklyEditorProps = {
  initialDayOfWeek: DayOfWeekProto
  proto: IWeeklyRepeatSchedule,
  onProtoChange: (proto: IWeeklyRepeatSchedule) => void,
}

const WeeklyEditor = (props: WeeklyEditorProps) => {

  return <>

    <div className={'WeeklyRepeatPeriod'}>
      Repeat every....
      <input type={'number'}
             value={props.proto.repeatPeriodWeeks || 1}
             onChange={event => props.onProtoChange({
               ...props.proto,
               repeatPeriodWeeks: parseInt(event.target.value)
             })}
      />
      Week(s)
    </div>

    <div className={'WeeklyRepeatContainer'}>
      {dayOfWeekValues.map(value => {

        // value is the actual ISO value according to the proto (MONDAY = 1, SUNDAY = 7)
        // name is SUNDAY, MONDAY
        let name: string = DayOfWeekProto[value];

        return <div className={'WeeklyRepeatItem'}>
              <input type={'checkbox'}
                     checked={((props.proto.daysOfWeekBitmask || 0) & toBitMask(value)) > 0}
                     onChange={event => props.onProtoChange({
                       ...props.proto,
                       daysOfWeekBitmask: (props.proto.daysOfWeekBitmask || 0) ^ toBitMask(value)
                     })}
              />{name.substring(0, 1)}
            </div>;
      })}
    </div>
  </>;


}